import { format } from "date-fns";
import _ from "lodash";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Search from "../../components/form/Search";
import MobileListActionBar from "../../components/mobile-action-bar/MobileListActionBar";
import Pagination from "../../components/Pagination";
import SortableHeader from "../../components/SortableHeader";
import useResponsiveLimit from "../../composables/useResponsiveLimit";
import { Role, useAuthStore } from "../../stores/auth";
import { WORK_ORDER_FILTER, WORK_ORDER_STATUS, useWorkOrdersStore } from "../../stores/work_orders";
import { formatDate, formatDateTime, formatTime } from "../../utils/dateFormat";
import { formatRange } from "../../utils/rangeFormat";

const formatAddress = (property) => {
  const address = property?.addresses?.[0];
  if (!address) return "";
  return [address.street, [address.zip, address.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

function WorkOrderRow({ workOrder, isBackoffice }) {
  const { t } = useTranslation();
  const untilPrefix = t("common.range_until");
  const scheduled = formatRange({
    from: formatDate(workOrder.scheduled_from),
    until: formatDate(workOrder.scheduled_until),
    untilPrefix,
  });
  const availability = formatRange({
    from: formatTime(workOrder.availability_from),
    until: formatTime(workOrder.availability_until),
    fromPrefix: t("common.range_from"),
    untilPrefix,
  });

  // Only open orders need attention; a lone date acts as both start and
  // end of the scheduled window.
  const today = format(new Date(), "yyyy-MM-dd");
  const scheduleStart = workOrder.scheduled_from || workOrder.scheduled_until;
  const scheduleEnd = workOrder.scheduled_until || workOrder.scheduled_from;
  const isOpen = workOrder.status === WORK_ORDER_STATUS.OPEN;
  const isOverdue = isOpen && scheduleEnd && today > scheduleEnd;
  const isDue = isOpen && !isOverdue && scheduleStart && scheduleStart <= today;
  const rowClass = isOverdue ? "table-danger" : isDue ? "table-warning" : "";
  const isExternal = !!workOrder.source && workOrder.source !== "portal";

  return (
    <tr className={rowClass}>
      <td>
        <Link to={`${workOrder.id}/details`} className="fw-bold">
          {workOrder.number}
        </Link>
        {isExternal && (
          <span
            className="badge bg-default text-dark ms-1"
            title={t(`work_orders.origin_hint.${workOrder.source}`)}
          >
            <i className="fa fa-clipboard-check fa-fw" />
          </span>
        )}
        {workOrder.comments?.length > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("menu.comments")}>
            <i className="fa fa-comments me-1" />
            {workOrder.comments.length}
          </span>
        )}
        {workOrder.files?.length > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("menu.files")}>
            <i className="fa fa-image fa-fw me-1"></i>
            {workOrder.files.length}
          </span>
        )}
        {workOrder.delivery_notes?.map((deliveryNote) => (
          <Link
            key={deliveryNote.id}
            to={`/delivery_notes/${deliveryNote.id}/details`}
            className="badge badge-link bg-default text-dark ms-1"
            title={`${t("delivery_notes.delivery_note")} ${deliveryNote.number}`}
          >
            <i className="fa fa-file-lines fa-fw" />
          </Link>
        ))}
      </td>
      {isBackoffice && <td className="d-none d-md-table-cell">{formatDateTime(workOrder.created_at)}</td>}
      <td>
        {scheduled}
        {availability && <div className="small text-muted">{availability}</div>}
      </td>
      <td>
        <ul className="list-group list-unstyled">
          {!_.isEmpty(workOrder.customer?.number) && <li>{workOrder.customer.number}</li>}
          {!_.isEmpty(workOrder.customer?.name) && <li>{workOrder.customer.name}</li>}
        </ul>
      </td>
      <td>
        <ul className="list-group list-unstyled">
          {!_.isEmpty(workOrder.customer_property?.name?.trim()) && <li>{workOrder.customer_property.name}</li>}
          <li>{formatAddress(workOrder.customer_property)}</li>
        </ul>
      </td>
      {isBackoffice && (
        <td>
          {workOrder.assigned_to ? (
            <>
              {workOrder.assigned_to.name}
              {workOrder.assigned_to.number && <small className="text-muted"> ({workOrder.assigned_to.number})</small>}
            </>
          ) : (
            <span className="text-muted">{t("work_orders.unassigned")}</span>
          )}
        </td>
      )}
    </tr>
  );
}

function WorkOrders() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useSearchParams();
  const hasRole = useAuthStore((state) => state.hasRole);
  const isBackoffice = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);

  const { byIds, allIds, total, fetchSome } = useWorkOrdersStore(
    useShallow((state) => ({
      byIds: state.byIds,
      allIds: state.allIds,
      total: state.total,
      fetchSome: state.fetchSome,
    })),
  );
  const workOrders = allIds.map((id) => byIds[id]).filter(Boolean);

  const defaultLimit = useResponsiveLimit(10);
  const limit = parseInt(queryParams.get("limit")) || defaultLimit;
  const page = parseInt(queryParams.get("page")) || 0;
  const offset = page * limit;
  const search = queryParams.get("q") || "";
  const filterParam = queryParams.get("filter");
  const defaultFilter = WORK_ORDER_FILTER.OPEN;
  const allowedFilters = Object.values(WORK_ORDER_FILTER);
  const normalizedFilter = allowedFilters.includes(filterParam) ? filterParam : defaultFilter;
  const backofficeOnlyFilters = [
    WORK_ORDER_FILTER.MINE,
    WORK_ORDER_FILTER.UNASSIGNED,
    WORK_ORDER_FILTER.CANCELED,
    WORK_ORDER_FILTER.ALL,
  ];
  const filter = !isBackoffice && backofficeOnlyFilters.includes(normalizedFilter)
    ? defaultFilter
    : normalizedFilter;
  const sortBy = queryParams.get("sort_by") || "scheduled";
  const sortDir = queryParams.get("sort_dir") || "asc";

  useEffect(() => {
    fetchSome({ q: search, filter, limit, offset, sort_by: sortBy, sort_dir: sortDir });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit, filter, sortBy, sortDir]);

  const paginationParams = new URLSearchParams(queryParams);
  paginationParams.set("filter", filter);

  const handleSearch = (e) => {
    setQueryParams(`page=0&q=${e.target.value}&filter=${filter}&sort_by=${sortBy}&sort_dir=${sortDir}`);
  };

  const setFilter = (nextFilter) => {
    setQueryParams(`page=0&q=${search}&filter=${nextFilter}&sort_by=${sortBy}&sort_dir=${sortDir}`);
  };

  const handleSort = (column) => {
    const newDir = sortBy === column && sortDir === "desc" ? "asc" : "desc";
    setQueryParams(`page=0&q=${search}&filter=${filter}&sort_by=${column}&sort_dir=${newDir}`);
  };

  const tabs = isBackoffice
    ? [
        { filter: WORK_ORDER_FILTER.OPEN, label: "work_orders.tabs.open" },
        { filter: WORK_ORDER_FILTER.UNASSIGNED, label: "work_orders.tabs.unassigned" },
        { filter: WORK_ORDER_FILTER.IN_PROGRESS, label: "work_orders.tabs.in_progress" },
        { filter: WORK_ORDER_FILTER.DONE, label: "work_orders.tabs.done" },
        { filter: WORK_ORDER_FILTER.CANCELED, label: "work_orders.tabs.canceled" },
        { filter: WORK_ORDER_FILTER.ALL, label: "work_orders.tabs.all" },
      ]
    : [
        { filter: WORK_ORDER_FILTER.OPEN, label: "work_orders.tabs.open" },
        { filter: WORK_ORDER_FILTER.IN_PROGRESS, label: "work_orders.tabs.in_progress" },
        { filter: WORK_ORDER_FILTER.DONE, label: "work_orders.tabs.done" },
      ];

  return (
    <div>
      <Breadcrumb items={[{ label: t("work_orders.work_orders") }]}>
        {isBackoffice && (
          <Link to="add" className="btn btn-red">
            <i className="fa fa-plus fa-fw me-1" />
            {t("work_orders.work_order")}
          </Link>
        )}
      </Breadcrumb>

      <MobileListActionBar>
        {isBackoffice && (
          <Link to="/work_orders/add" className="btn btn-red flex-fill">
            <i className="fa fa-plus fa-fw me-1" />
            {t("work_orders.work_order")}
          </Link>
        )}
      </MobileListActionBar>

      <div className="card border-0">
        <div className="d-md-none px-3 pt-3">
          <select className="form-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            {tabs.map((tab) => (
              <option key={tab.filter} value={tab.filter}>{t(tab.label)}</option>
            ))}
          </select>
        </div>
        <ul className="nav nav-tabs nav-tabs-v2 px-3 d-none d-md-flex">
          {tabs.map((tab) => (
            <li key={tab.filter} className="nav-item me-2">
              <button onClick={() => setFilter(tab.filter)} className={`nav-link px-2 ${filter === tab.filter && "active"}`}>
                {t(tab.label)}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <Search handleSearch={handleSearch} search={search} />
            <div className="table-responsive mb-3">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <SortableHeader column="number" label={t("work_orders.number")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    {isBackoffice && (
                      <SortableHeader column="created_at" label={t("work_orders.created_at")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="d-none d-md-table-cell" />
                    )}
                    <SortableHeader column="scheduled" label={t("work_orders.scheduled_short")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th>{t("customers.customer")}</th>
                    <th>{t("customers.properties.property")}</th>
                    {isBackoffice && (
                      <SortableHeader column="assigned_to" label={t("work_orders.assigned_to")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((workOrder) => (
                    <WorkOrderRow
                      key={workOrder.id}
                      workOrder={workOrder}
                      isBackoffice={isBackoffice}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              basePath="/work_orders"
              page={page}
              limit={limit}
              total={total}
              queryParams={paginationParams}
              label={t("work_orders.work_orders")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkOrders;
